import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, EmptyStateIcons, Icons, Search, showAlert, Table } from '@silverfort/sf-components';
import Filters from './Filters/Filters';
import useGetRefSize from '../../../hooks/useGetRefSize';
import useGetIncidents from '../api/queries/useGetIncidents';
import moment from 'moment';
import { Incident } from '../api/types/Incidents';
import { INCIDENT_STATUS, INCIDENT_TYPE } from '@silverfort-nodelibs/common/utils/consts';
import { getEnumLabelByValue, getEnumPropByValue } from '@silverfort-nodelibs/common/utils/common';
import useIncidentStatusUpdate from '../api/mutations/useIncidentStatusUpdate';
import IncidentStatusSnackbar from './IncidentStatusSnackbar';
import useGetColumns from './columns/useGetColumns';
import useGetFilters from './Filters/useGetFilters';
import ItdrContext from '../ItdrContext';
import { Entity } from '../../../api';
import MainEntitiesSearch from './MainEntitiesSearch/MainEntitiesSearch';
import { useGetIncidentReport } from '../api/queries/useGetIncidentsReport';

const INCIDENT_PAGE_SIZE = 20;

const getRows = (incidents: Incident[]) => {
    return incidents;
};

interface Props {
    emptyStateTitle?: string;
    emptyStateDescription?: string;
}

const IncidentsTable: FC<Props> = ({ emptyStateTitle, emptyStateDescription }) => {
    const [sortColumn, setSortColumn] = useState('last_log_timestamp');
    const [sortDirection, setSortDirection] = useState('desc');
    const [types, setTypes] = useState<number[]>();
    const [searchedIncidentId, setSearchedIncidentId] = useState<string>();
    const [selectedIncidents, setSelectedIncidents] = useState<string[]>([]);
    const [mainEntitiesGuids, setMainEntitiesGuids] = useState<string[]>();

    const {
        lastActivityDiffInDays,
        hiddenTypes,
        refreshGraph,
        selectedSeverities,
        setSelectedSeverities,
        selectedStatuses,
        setSelectedStatuses
    } = useContext(ItdrContext);

    const allFilters = useGetFilters({
        selectedSeverities,
        setSelectedSeverities,
        selectedStatuses,
        setSelectedStatuses,
        types,
        setTypes,
        hiddenTypes
    });

    const tableRef = useRef(null);
    const { height: tableHeight } = useGetRefSize(tableRef);
    const kindsFilter = useMemo(() => {
        return types?.map((type) => getEnumPropByValue(INCIDENT_TYPE, type, 'apiName'));
    }, [types]);

    const lastActivityTimestamp = useMemo(
        () => moment().subtract(lastActivityDiffInDays, 'days').toDate().getTime(),
        [lastActivityDiffInDays]
    );

    const { data, isFetching, fetchNextPage, refreshTable } = useGetIncidents({
        searchedIncidentId,
        kinds: kindsFilter,
        statuses: selectedStatuses,
        severities: selectedSeverities,
        pageSize: INCIDENT_PAGE_SIZE,
        sortField: sortColumn,
        sortDirection,
        showExcluded: false,
        fromLastLogTimestamp: lastActivityTimestamp,
        mainEntitiesGuids: mainEntitiesGuids
    });

    const { downloadReport, downloadingReport } = useGetIncidentReport({
        searchedIncidentId,
        kinds: kindsFilter,
        statuses: selectedStatuses,
        severities: selectedSeverities,
        sortField: sortColumn,
        sortDirection,
        showExcluded:false,
        fromLastLogTimestamp: lastActivityTimestamp,
        mainEntitiesGuids: mainEntitiesGuids
    });

    useEffect(() => {
        if (!data?.length) return;

        const visibleIds = new Set(data.map((incident) => incident.id));
        setSelectedIncidents((prevSelected) => prevSelected.filter((id) => visibleIds.has(id)));
    }, [data]);

    const onUpdateSuccessfully = useCallback(
        (incidentIds: string[], status: number) => {
            refreshTable();
            setSelectedIncidents([]);
            showAlert(
                `Status of ${incidentIds.length} incidents changed to ${getEnumLabelByValue(INCIDENT_STATUS, status)}`,
                'success'
            );
            refreshGraph();
        },
        [refreshTable, refreshGraph]
    );

    const onUpdateFailed = useCallback(() => {
        showAlert('Failed to update incidents status', 'error');
    }, []);

    const { mutate, isLoading } = useIncidentStatusUpdate({ onSuccess: onUpdateSuccessfully, onError: onUpdateFailed });
    const columns = useGetColumns(mutate);

    const handleSort = (columnKey: string, sortDirection: string) => {
        setSortColumn(columnKey);
        setSortDirection(sortDirection);
    };

    const onSearchFieldTyping = (searchValue: string) => {
        setSearchedIncidentId(searchValue);
    };

    const onMainEntitiesFilterSubmit = (mainEntities: Entity[]) => {
        setMainEntitiesGuids(mainEntities.length ? mainEntities.map((entity) => entity.guid) : undefined);
    };

    const rows = getRows(data ?? []);

    return (
        <div className="h-full w-1 flex flex-col flex-1 gap-4">
            <div className="h-fit w-full flex justify-between flex-none">
                <div className="flex items-center gap-2 flex-grow">
                    <Filters
                        options={allFilters ?? []}
                        customFilters={[<MainEntitiesSearch onFilterSubmit={onMainEntitiesFilterSubmit} />]}
                    />
                </div>
                <div className="w-75 h-full flex justify-end gap-2 flex-none">
                    <Search onChange={onSearchFieldTyping} text={searchedIncidentId} placeholder="Search by ID" />
                    <Button
                        disabled={!rows.length}
                        iconRight={Icons.Download}
                        isLoading={downloadingReport}
                        onClick={downloadReport}
                        title="Export incidents"
                    />
                </div>
            </div>
            <div className="h-1 w-full flex-grow flex-shrink" ref={tableRef}>
                <Table
                    loading={isFetching || isLoading}
                    columns={columns}
                    bodyHeight={tableHeight ? tableHeight - 56 : 0}
                    onScrollEnd={fetchNextPage}
                    rows={rows}
                    EmptyStateProps={{
                        className: 'w-full h-max',
                        title: !searchedIncidentId ? emptyStateTitle ?? 'No Incidents' : 'No Results',
                        description: !searchedIncidentId
                            ? emptyStateDescription ?? 'Great job keeping your network clean'
                            : 'You may need to adjust your search',
                        icon: !searchedIncidentId ? EmptyStateIcons.TaskDone : EmptyStateIcons.NoResults
                    }}
                    tableContainerClassName="w-full h-full"
                    onSortChange={handleSort}
                    keyExtractor={(row) => row?.id}
                    selectable
                    selected={selectedIncidents}
                    onSelect={(selected) => setSelectedIncidents([...new Set(selected)])}
                />
                <IncidentStatusSnackbar
                    isOpen={!!selectedIncidents?.length}
                    onClose={() => setSelectedIncidents([])}
                    selectedIncidents={selectedIncidents}
                    onStatusChange={(incidentIds, status) => {
                        mutate(incidentIds, status);
                        refreshTable();
                    }}
                />
            </div>
        </div>
    );
};

export default IncidentsTable;
